import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import {
  useAdminScope,
} from "../../../context/AdminScopeContext";

import {
  AdminButton,
} from "../../../pages/admin/action_engine";

import {
  AdminSection,
  AdminSectionHeader,
  AdminSectionTitle,
} from "../../../pages/admin/section_engine";

import {
  useAdminConfirmation,
} from "../../../pages/admin/panel_engine/consumer";

import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
  AdminSuccessState,
} from "../../../pages/admin/ui_engine";

import {
  archiveSupplierDocument,
  downloadSupplierDocument,
  listSupplierDocuments,
  updateSupplierDocument,
  uploadSupplierDocument,
} from "../../../services/supplierDocumentsAdminApi";


export const SUPPLIER_DOCUMENT_TYPE_OPTIONS =
  Object.freeze([
    {
      value: "CONTRACT",
      label: "Contrat",
    },
    {
      value: "INVOICE",
      label: "Facture",
    },
    {
      value: "CONFIRMATION",
      label: "Confirmation",
    },
    {
      value: "COMMERCIAL_TERMS",
      label: "Conditions commerciales",
    },
    {
      value: "CATALOG",
      label: "Catalogue",
    },
    {
      value: "PRICE_LIST",
      label: "Liste de prix",
    },
    {
      value: "API_DOCUMENTATION",
      label: "Documentation API",
    },
    {
      value: "SFTP_DOCUMENTATION",
      label: "Documentation SFTP",
    },
    {
      value: "ORDER_EXAMPLE",
      label: "Exemple de commande",
    },
    {
      value: "NOTE",
      label: "Note",
    },
    {
      value: "OTHER",
      label: "Autre",
    },
  ]);


function normalizeIdentifier(value) {
  if (
    value === undefined
    || value === null
  ) {
    return "";
  }

  return String(value).trim();
}


function resolveErrorMessage(
  error,
  fallback,
) {
  return (
    error?.response?.data?.message
    || error?.response?.data?.error_description
    || error?.response?.data?.error
    || error?.message
    || fallback
  );
}


function formatSize(value) {
  const size = Number(value);

  if (
    !Number.isFinite(size)
    || size < 0
  ) {
    return "—";
  }

  if (size < 1024) {
    return `${size} o`;
  }

  if (size < 1024 * 1024) {
    return `${(
      size / 1024
    ).toFixed(1)} Ko`;
  }

  return `${(
    size / (1024 * 1024)
  ).toFixed(1)} Mo`;
}


function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return date.toLocaleString(
    "fr-FR",
  );
}


function documentKey(
  document,
  index,
) {
  return (
    document?.uuid
    || document?.document_uuid
    || `${document?.original_filename || "document"}-${index}`
  );
}


export default function SupplierDocumentsWorkspace({
  supplierUuid,
  supplierName = "",
}) {
  const scope = useAdminScope();

  const companyUuid = normalizeIdentifier(
    scope?.selectedCompanyUuid
    || scope?.companyUuid
    || scope?.selectedCompany?.uuid,
  );

  const activityUuid = normalizeIdentifier(
    scope?.selectedActivityUuid
    || scope?.activityUuid
    || scope?.selectedActivity?.uuid,
  );

  const normalizedSupplierUuid =
    useMemo(
      () => normalizeIdentifier(
        supplierUuid,
      ),
      [supplierUuid],
    );

  const [
    documents,
    setDocuments,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    loadError,
    setLoadError,
  ] = useState("");

  const [
    uploadError,
    setUploadError,
  ] = useState("");

  const [
    uploadSuccess,
    setUploadSuccess,
  ] = useState("");

  const [
    selectedFile,
    setSelectedFile,
  ] = useState(null);

  const [
    documentType,
    setDocumentType,
  ] = useState("");

  const [
    title,
    setTitle,
  ] = useState("");

  const [
    notes,
    setNotes,
  ] = useState("");

  const [
    uploading,
    setUploading,
  ] = useState(false);

  const [
    dragging,
    setDragging,
  ] = useState(false);

  const fileInputRef =
    useRef(null);

  const confirm =
    useAdminConfirmation();

  const [
    editingDocumentUuid,
    setEditingDocumentUuid,
  ] = useState("");

  const [
    editDocumentType,
    setEditDocumentType,
  ] = useState("");

  const [
    editTitle,
    setEditTitle,
  ] = useState("");

  const [
    editNotes,
    setEditNotes,
  ] = useState("");

  const [
    editIsActive,
    setEditIsActive,
  ] = useState(true);

  const [
    lifecyclePendingUuid,
    setLifecyclePendingUuid,
  ] = useState("");

  const [
    lifecycleError,
    setLifecycleError,
  ] = useState("");

  const [
    lifecycleSuccess,
    setLifecycleSuccess,
  ] = useState("");


  const hasScope = Boolean(
    companyUuid
    && activityUuid,
  );


  const loadDocuments =
    useCallback(
      async () => {
        if (
          !normalizedSupplierUuid
          || !hasScope
        ) {
          setDocuments([]);
          setLoadError("");
          return;
        }

        setLoading(true);
        setLoadError("");

        try {
          const items =
            await listSupplierDocuments({
              supplierUuid:
                normalizedSupplierUuid,
              companyUuid,
              activityUuid,
            });

          setDocuments(
            Array.isArray(items)
              ? items
              : [],
          );
        } catch (error) {
          setDocuments([]);

          setLoadError(
            resolveErrorMessage(
              error,
              "Impossible de charger "
              + "les documents fournisseur.",
            ),
          );
        } finally {
          setLoading(false);
        }
      },
      [
        activityUuid,
        companyUuid,
        hasScope,
        normalizedSupplierUuid,
      ],
    );


  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);


  const selectFiles =
    useCallback(
      (fileList) => {
        setUploadError("");
        setUploadSuccess("");

        const files =
          Array.from(
            fileList || [],
          );

        if (files.length === 0) {
          setSelectedFile(null);
          return;
        }

        if (files.length > 1) {
          setSelectedFile(null);

          setUploadError(
            "Un seul fichier peut être "
            + "déposé à la fois.",
          );

          return;
        }

        setSelectedFile(
          files[0],
        );
      },
      [],
    );


  const handleFileChange =
    useCallback(
      (event) => {
        selectFiles(
          event.target.files,
        );
      },
      [selectFiles],
    );


  const handleDragEnter =
    useCallback(
      (event) => {
        event.preventDefault();

        if (!uploading) {
          setDragging(true);
        }
      },
      [uploading],
    );


  const handleDragOver =
    useCallback(
      (event) => {
        event.preventDefault();

        if (!uploading) {
          setDragging(true);
        }
      },
      [uploading],
    );


  const handleDragLeave =
    useCallback(
      (event) => {
        event.preventDefault();
        setDragging(false);
      },
      [],
    );


  const handleDrop =
    useCallback(
      (event) => {
        event.preventDefault();
        setDragging(false);

        if (uploading) {
          return;
        }

        selectFiles(
          event.dataTransfer?.files,
        );
      },
      [
        selectFiles,
        uploading,
      ],
    );


  const resetUploadForm =
    useCallback(
      () => {
        setSelectedFile(null);
        setDocumentType("");
        setTitle("");
        setNotes("");

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
      [],
    );


  const handleSubmit =
    useCallback(
      async (event) => {
        event.preventDefault();

        if (uploading) {
          return;
        }

        setUploadError("");
        setUploadSuccess("");

        if (!selectedFile) {
          setUploadError(
            "Sélectionnez un fichier.",
          );

          return;
        }

        if (!documentType) {
          setUploadError(
            "Sélectionnez un type "
            + "de document.",
          );

          return;
        }

        if (
          !normalizedSupplierUuid
          || !hasScope
        ) {
          setUploadError(
            "Le scope société, activité "
            + "et fournisseur est requis.",
          );

          return;
        }

        setUploading(true);

        try {
          await uploadSupplierDocument({
            supplierUuid:
              normalizedSupplierUuid,
            companyUuid,
            activityUuid,
            file:
              selectedFile,
            documentType,
            title:
              title.trim(),
            notes:
              notes.trim(),
          });

          const filename =
            selectedFile.name;

          resetUploadForm();

          setUploadSuccess(
            `Document ${filename} ajouté.`,
          );

          await loadDocuments();
        } catch (error) {
          setUploadError(
            resolveErrorMessage(
              error,
              "Impossible d'ajouter "
              + "le document fournisseur.",
            ),
          );
        } finally {
          setUploading(false);
        }
      },
      [
        activityUuid,
        companyUuid,
        documentType,
        hasScope,
        loadDocuments,
        normalizedSupplierUuid,
        notes,
        resetUploadForm,
        selectedFile,
        title,
        uploading,
      ],
    );


  const resolveDocumentUuid =
    useCallback(
      (document) => (
        normalizeIdentifier(
          document?.document_uuid
          || document?.document?.uuid
        )
      ),
      [],
    );


  const startEditing =
    useCallback(
      (document) => {
        const documentUuid =
          resolveDocumentUuid(
            document,
          );

        if (!documentUuid) {
          setLifecycleError(
            "Identifiant du document "
            + "indisponible.",
          );
          return;
        }

        setLifecycleError("");
        setLifecycleSuccess("");

        setEditingDocumentUuid(
          documentUuid,
        );

        setEditDocumentType(
          document.document_type
          || "OTHER",
        );

        setEditTitle(
          document.title
          || "",
        );

        setEditNotes(
          document.notes
          || "",
        );

        setEditIsActive(
          document.is_active
          !== false,
        );
      },
      [resolveDocumentUuid],
    );


  const cancelEditing =
    useCallback(
      () => {
        setEditingDocumentUuid("");
        setEditDocumentType("");
        setEditTitle("");
        setEditNotes("");
        setEditIsActive(true);
      },
      [],
    );


  const handleDownload =
    useCallback(
      async (document) => {
        const documentUuid =
          resolveDocumentUuid(
            document,
          );

        if (
          !documentUuid
          || lifecyclePendingUuid
        ) {
          return;
        }

        setLifecycleError("");
        setLifecycleSuccess("");
        setLifecyclePendingUuid(
          documentUuid,
        );

        try {
          const response =
            await downloadSupplierDocument({
              supplierUuid:
                normalizedSupplierUuid,
              documentUuid,
              companyUuid,
              activityUuid,
            });

          const blob =
            response?.data;

          if (!blob) {
            throw new Error(
              "Contenu documentaire absent.",
            );
          }

          const objectUrl =
            window.URL.createObjectURL(
              blob,
            );

          const link =
            window.document
              .createElement("a");

          link.href =
            objectUrl;

          link.download =
            document.original_filename
            || "document";

          window.document.body
            .appendChild(
              link,
            );

          link.click();
          link.remove();

          window.URL.revokeObjectURL(
            objectUrl,
          );
        } catch (error) {
          setLifecycleError(
            resolveErrorMessage(
              error,
              "Impossible de télécharger "
              + "le document fournisseur.",
            ),
          );
        } finally {
          setLifecyclePendingUuid("");
        }
      },
      [
        activityUuid,
        companyUuid,
        lifecyclePendingUuid,
        normalizedSupplierUuid,
        resolveDocumentUuid,
      ],
    );


  const handleSaveLifecycle =
    useCallback(
      async (document) => {
        const documentUuid =
          resolveDocumentUuid(
            document,
          );

        if (
          !documentUuid
          || lifecyclePendingUuid
        ) {
          return;
        }

        setLifecycleError("");
        setLifecycleSuccess("");
        setLifecyclePendingUuid(
          documentUuid,
        );

        try {
          await updateSupplierDocument({
            supplierUuid:
              normalizedSupplierUuid,
            documentUuid,
            companyUuid,
            activityUuid,
            documentType:
              editDocumentType,
            title:
              editTitle.trim(),
            notes:
              editNotes.trim(),
            isActive:
              editIsActive,
          });

          cancelEditing();

          setLifecycleSuccess(
            "Document fournisseur mis à jour.",
          );

          await loadDocuments();
        } catch (error) {
          setLifecycleError(
            resolveErrorMessage(
              error,
              "Impossible de modifier "
              + "le document fournisseur.",
            ),
          );
        } finally {
          setLifecyclePendingUuid("");
        }
      },
      [
        activityUuid,
        cancelEditing,
        companyUuid,
        editDocumentType,
        editIsActive,
        editNotes,
        editTitle,
        lifecyclePendingUuid,
        loadDocuments,
        normalizedSupplierUuid,
        resolveDocumentUuid,
      ],
    );


  const handleArchive =
    useCallback(
      async (document) => {
        const documentUuid =
          resolveDocumentUuid(
            document,
          );

        if (
          !documentUuid
          || lifecyclePendingUuid
        ) {
          return;
        }

        const accepted =
          await confirm({
            title:
              "Archiver le document",
            message:
              "Le document sera retiré "
              + "du fournisseur. "
              + "Le fichier physique "
              + "ne sera pas supprimé.",
            confirmLabel:
              "Archiver",
            cancelLabel:
              "Annuler",
          });

        if (!accepted) {
          return;
        }

        setLifecycleError("");
        setLifecycleSuccess("");
        setLifecyclePendingUuid(
          documentUuid,
        );

        try {
          await archiveSupplierDocument({
            supplierUuid:
              normalizedSupplierUuid,
            documentUuid,
            companyUuid,
            activityUuid,
          });

          if (
            editingDocumentUuid
            === documentUuid
          ) {
            cancelEditing();
          }

          setLifecycleSuccess(
            "Document fournisseur archivé.",
          );

          await loadDocuments();
        } catch (error) {
          setLifecycleError(
            resolveErrorMessage(
              error,
              "Impossible d'archiver "
              + "le document fournisseur.",
            ),
          );
        } finally {
          setLifecyclePendingUuid("");
        }
      },
      [
        activityUuid,
        cancelEditing,
        companyUuid,
        confirm,
        editingDocumentUuid,
        lifecyclePendingUuid,
        loadDocuments,
        normalizedSupplierUuid,
        resolveDocumentUuid,
      ],
    );


  if (!normalizedSupplierUuid) {
    return null;
  }


  return (
    <AdminSection
      data-testid="supplier-documents-workspace"
      data-supplier-uuid={
        normalizedSupplierUuid
      }
    >
      <AdminSectionHeader>
        <HeaderContent>
          <div>
            <AdminSectionTitle>
              Documents fournisseur
            </AdminSectionTitle>

            {supplierName && (
              <SupplierName>
                {supplierName}
              </SupplierName>
            )}
          </div>
        </HeaderContent>
      </AdminSectionHeader>


      {!hasScope && (
        <AdminEmptyState
          title="Scope requis"
          message={
            "Sélectionnez une société "
            + "et une activité pour "
            + "gérer les documents."
          }
          compact
        />
      )}


      {hasScope && (
        <>
          <UploadForm
            onSubmit={
              handleSubmit
            }
            data-testid={
              "supplier-documents-upload-form"
            }
          >
            <UploadHeading>
              Ajouter un document
            </UploadHeading>

            <FieldGrid>
              <Field>
                <FieldLabel
                  htmlFor={
                    "supplier-document-type"
                  }
                >
                  Type de document
                </FieldLabel>

                <Select
                  id={
                    "supplier-document-type"
                  }
                  value={
                    documentType
                  }
                  onChange={
                    (event) => {
                      setDocumentType(
                        event.target.value,
                      );
                    }
                  }
                  disabled={
                    uploading
                  }
                >
                  <option value="">
                    Sélectionner un type
                  </option>

                  {
                    SUPPLIER_DOCUMENT_TYPE_OPTIONS
                      .map(
                        (option) => (
                          <option
                            key={
                              option.value
                            }
                            value={
                              option.value
                            }
                          >
                            {option.label}
                          </option>
                        ),
                      )
                  }
                </Select>
              </Field>

              <Field>
                <FieldLabel
                  htmlFor={
                    "supplier-document-title"
                  }
                >
                  Titre
                </FieldLabel>

                <Input
                  id={
                    "supplier-document-title"
                  }
                  type="text"
                  value={title}
                  onChange={
                    (event) => {
                      setTitle(
                        event.target.value,
                      );
                    }
                  }
                  disabled={
                    uploading
                  }
                  placeholder="Optionnel"
                />
              </Field>
            </FieldGrid>

            <Field>
              <FieldLabel
                htmlFor={
                  "supplier-document-notes"
                }
              >
                Notes
              </FieldLabel>

              <TextArea
                id={
                  "supplier-document-notes"
                }
                value={notes}
                onChange={
                  (event) => {
                    setNotes(
                      event.target.value,
                    );
                  }
                }
                disabled={
                  uploading
                }
                placeholder="Optionnel"
                rows={3}
              />
            </Field>

            <DropArea
              data-testid={
                "supplier-documents-dropzone"
              }
              $dragging={
                dragging
              }
              $disabled={
                uploading
              }
              onDragEnter={
                handleDragEnter
              }
              onDragOver={
                handleDragOver
              }
              onDragLeave={
                handleDragLeave
              }
              onDrop={
                handleDrop
              }
            >
              <DropTitle>
                Déposez un fichier ici
              </DropTitle>

              <DropHint>
                ou sélectionnez-le
                depuis votre ordinateur.
              </DropHint>

              <FileInput
                ref={
                  fileInputRef
                }
                id={
                  "supplier-document-file"
                }
                data-testid={
                  "supplier-documents-file-input"
                }
                type="file"
                onChange={
                  handleFileChange
                }
                disabled={
                  uploading
                }
              />

              {selectedFile && (
                <SelectedFile
                  data-testid={
                    "supplier-documents-selected-file"
                  }
                >
                  {
                    selectedFile.name
                  }
                </SelectedFile>
              )}
            </DropArea>

            {uploadError && (
              <UploadState
                role="alert"
                data-testid={
                  "supplier-documents-upload-error"
                }
              >
                {uploadError}
              </UploadState>
            )}

            {uploadSuccess && (
              <AdminSuccessState
                role="status"
                data-testid={
                  "supplier-documents-upload-success"
                }
              >
                {uploadSuccess}
              </AdminSuccessState>
            )}

            <UploadToolbar>
              <AdminButton
                type="submit"
                variant="primary"
                disabled={
                  uploading
                }
              >
                {
                  uploading
                    ? "Envoi en cours..."
                    : "Ajouter le document"
                }
              </AdminButton>
            </UploadToolbar>
          </UploadForm>


          {lifecycleError && (
            <UploadState
              role="alert"
              data-testid={
                "supplier-documents-lifecycle-error"
              }
            >
              {lifecycleError}
            </UploadState>
          )}

          {lifecycleSuccess && (
            <AdminSuccessState
              role="status"
              data-testid={
                "supplier-documents-lifecycle-success"
              }
            >
              {lifecycleSuccess}
            </AdminSuccessState>
          )}


          {loadError && (
            <StateWrapper
              data-testid={
                "supplier-documents-error"
              }
            >
              <AdminErrorState
                message={
                  loadError
                }
                compact
              />
            </StateWrapper>
          )}


          {loading && (
            <StateWrapper
              data-testid={
                "supplier-documents-loading"
              }
            >
              <AdminLoadingState
                message={
                  "Chargement des documents "
                  + "fournisseur..."
                }
              />
            </StateWrapper>
          )}


          {!loading
            && !loadError
            && documents.length === 0
            && (
              <StateWrapper
                data-testid={
                  "supplier-documents-empty"
                }
              >
                <AdminEmptyState
                  title={
                    "Aucun document fournisseur"
                  }
                  message={
                    "Ajoutez un contrat, une facture, "
                    + "une confirmation, un catalogue "
                    + "ou tout autre document utile."
                  }
                  compact
                />
              </StateWrapper>
            )}


          {!loading
            && !loadError
            && documents.length > 0
            && (
              <DocumentList
                data-testid={
                  "supplier-documents-list"
                }
              >
                {
                  documents.map(
                    (
                      document,
                      index,
                    ) => (
                      <DocumentCard
                        key={
                          documentKey(
                            document,
                            index,
                          )
                        }
                      >
                        <DocumentHeader>
                          <div>
                            <DocumentFilename>
                              {
                                document.original_filename
                                || "Document"
                              }
                            </DocumentFilename>

                            <DocumentType>
                              {
                                document.document_type
                                || "OTHER"
                              }
                            </DocumentType>
                          </div>

                          <DocumentMeta>
                            {
                              formatSize(
                                document.size_bytes,
                              )
                            }
                          </DocumentMeta>
                        </DocumentHeader>

                        {
                          document.title
                          && (
                            <DocumentTitle>
                              {document.title}
                            </DocumentTitle>
                          )
                        }

                        {
                          document.notes
                          && (
                            <DocumentNotes>
                              {document.notes}
                            </DocumentNotes>
                          )
                        }

                        <DocumentFooter>
                          <span>
                            {
                              document.media_type
                              || "Type inconnu"
                            }
                          </span>

                          <span>
                            {
                              formatDate(
                                document.created_at,
                              )
                            }
                          </span>
                        </DocumentFooter>

                        {
                          editingDocumentUuid
                          === resolveDocumentUuid(
                            document,
                          )
                            ? (
                              <LifecycleEditor
                                data-testid={
                                  "supplier-document-editor"
                                }
                              >
                                <Field>
                                  <FieldLabel>
                                    Classification
                                  </FieldLabel>

                                  <Select
                                    value={
                                      editDocumentType
                                    }
                                    onChange={
                                      (event) => {
                                        setEditDocumentType(
                                          event.target.value,
                                        );
                                      }
                                    }
                                  >
                                    {
                                      SUPPLIER_DOCUMENT_TYPE_OPTIONS
                                        .map(
                                          (option) => (
                                            <option
                                              key={
                                                option.value
                                              }
                                              value={
                                                option.value
                                              }
                                            >
                                              {option.label}
                                            </option>
                                          ),
                                        )
                                    }
                                  </Select>
                                </Field>

                                <Field>
                                  <FieldLabel>
                                    Titre
                                  </FieldLabel>

                                  <Input
                                    value={
                                      editTitle
                                    }
                                    onChange={
                                      (event) => {
                                        setEditTitle(
                                          event.target.value,
                                        );
                                      }
                                    }
                                  />
                                </Field>

                                <Field>
                                  <FieldLabel>
                                    Notes
                                  </FieldLabel>

                                  <TextArea
                                    rows={3}
                                    value={
                                      editNotes
                                    }
                                    onChange={
                                      (event) => {
                                        setEditNotes(
                                          event.target.value,
                                        );
                                      }
                                    }
                                  />
                                </Field>

                                <LifecycleCheckbox>
                                  <input
                                    type="checkbox"
                                    checked={
                                      editIsActive
                                    }
                                    onChange={
                                      (event) => {
                                        setEditIsActive(
                                          event.target.checked,
                                        );
                                      }
                                    }
                                  />

                                  Document actif
                                </LifecycleCheckbox>

                                <DocumentActions>
                                  <AdminButton
                                    type="button"
                                    variant="primary"
                                    onClick={
                                      () => (
                                        handleSaveLifecycle(
                                          document,
                                        )
                                      )
                                    }
                                    disabled={
                                      Boolean(
                                        lifecyclePendingUuid
                                      )
                                    }
                                  >
                                    Enregistrer
                                  </AdminButton>

                                  <AdminButton
                                    type="button"
                                    variant="ghost"
                                    onClick={
                                      cancelEditing
                                    }
                                  >
                                    Annuler
                                  </AdminButton>
                                </DocumentActions>
                              </LifecycleEditor>
                            )
                            : (
                              <DocumentActions>
                                <AdminButton
                                  type="button"
                                  variant="ghost"
                                  onClick={
                                    () => (
                                      handleDownload(
                                        document,
                                      )
                                    )
                                  }
                                  disabled={
                                    Boolean(
                                      lifecyclePendingUuid
                                    )
                                  }
                                >
                                  Télécharger
                                </AdminButton>

                                <AdminButton
                                  type="button"
                                  variant="ghost"
                                  onClick={
                                    () => (
                                      startEditing(
                                        document,
                                      )
                                    )
                                  }
                                  disabled={
                                    Boolean(
                                      lifecyclePendingUuid
                                    )
                                  }
                                >
                                  Modifier
                                </AdminButton>

                                <AdminButton
                                  type="button"
                                  variant="ghost"
                                  onClick={
                                    () => (
                                      handleArchive(
                                        document,
                                      )
                                    )
                                  }
                                  disabled={
                                    Boolean(
                                      lifecyclePendingUuid
                                    )
                                  }
                                >
                                  Archiver
                                </AdminButton>
                              </DocumentActions>
                            )
                        }
                      </DocumentCard>
                    ),
                  )
                }
              </DocumentList>
            )}
        </>
      )}
    </AdminSection>
  );
}


const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
`;


const SupplierName = styled.p`
  margin: 0.35rem 0 0;
  color: rgba(247, 247, 247, 0.62);
  font-size: 0.88rem;
`;


const UploadForm = styled.form`
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid
    rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  background: rgba(18, 25, 43, 0.72);
`;


const UploadHeading = styled.h4`
  margin: 0;
  color: #f7f7f7;
  font-size: 0.95rem;
`;


const FieldGrid = styled.div`
  display: grid;
  grid-template-columns:
    repeat(
      auto-fit,
      minmax(220px, 1fr)
    );
  gap: 1rem;
`;


const Field = styled.div`
  display: grid;
  gap: 0.4rem;
`;


const FieldLabel = styled.label`
  color: rgba(247, 247, 247, 0.76);
  font-size: 0.82rem;
  font-weight: 700;
`;


const fieldStyles = `
  width: 100%;
  box-sizing: border-box;
  border: 1px solid
    rgba(255, 255, 255, 0.13);
  border-radius: 10px;
  background: #151d31;
  color: #f7f7f7;
  outline: none;

  &:focus {
    border-color:
      rgba(255, 249, 234, 0.65);
  }

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
`;


const Input = styled.input`
  ${fieldStyles}
  min-height: 42px;
  padding: 0.65rem 0.75rem;
`;


const Select = styled.select`
  ${fieldStyles}
  min-height: 42px;
  padding: 0.65rem 0.75rem;
`;


const TextArea = styled.textarea`
  ${fieldStyles}
  padding: 0.65rem 0.75rem;
  resize: vertical;
`;


const DropArea = styled.div`
  display: grid;
  justify-items: center;
  gap: 0.65rem;
  padding: 1.4rem;
  border:
    2px dashed
    ${({ $dragging }) => (
      $dragging
        ? "rgba(140, 224, 178, 0.85)"
        : "rgba(255, 255, 255, 0.18)"
    )};
  border-radius: 14px;
  background:
    ${({ $dragging }) => (
      $dragging
        ? "rgba(73, 184, 126, 0.10)"
        : "rgba(255, 255, 255, 0.025)"
    )};
  opacity:
    ${({ $disabled }) => (
      $disabled
        ? 0.58
        : 1
    )};
`;


const DropTitle = styled.strong`
  color: #f7f7f7;
`;


const DropHint = styled.span`
  color: rgba(247, 247, 247, 0.62);
  font-size: 0.82rem;
  text-align: center;
`;


const FileInput = styled.input`
  max-width: 100%;
  color: rgba(247, 247, 247, 0.82);
`;


const SelectedFile = styled.div`
  max-width: 100%;
  overflow-wrap: anywhere;
  color: #8ce0b2;
  font-size: 0.85rem;
  font-weight: 700;
`;


const UploadToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
`;


const UploadState = styled.div`
  padding: 0.75rem;
  border-radius: 10px;
  background: rgba(225, 95, 95, 0.12);
  color: #ffaaaa;
`;


const StateWrapper = styled.div`
  margin-top: 1rem;
`;


const DocumentList = styled.div`
  display: grid;
  gap: 0.8rem;
  margin-top: 1rem;
`;


const DocumentCard = styled.article`
  display: grid;
  gap: 0.65rem;
  padding: 1rem;
  border: 1px solid
    rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: #202a43;
`;


const DocumentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;


const DocumentFilename = styled.strong`
  display: block;
  color: #f7f7f7;
  overflow-wrap: anywhere;
`;


const DocumentType = styled.span`
  display: inline-flex;
  margin-top: 0.35rem;
  padding: 0.25rem 0.45rem;
  border-radius: 999px;
  background:
    rgba(255, 255, 255, 0.07);
  color:
    rgba(247, 247, 247, 0.68);
  font-size: 0.72rem;
  font-weight: 800;
`;


const DocumentMeta = styled.span`
  white-space: nowrap;
  color: rgba(247, 247, 247, 0.62);
  font-size: 0.82rem;
`;


const DocumentTitle = styled.div`
  color: rgba(247, 247, 247, 0.88);
  font-weight: 700;
`;


const DocumentNotes = styled.p`
  margin: 0;
  color: rgba(247, 247, 247, 0.66);
  white-space: pre-wrap;
`;


const DocumentFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.5rem;
  color: rgba(247, 247, 247, 0.52);
  font-size: 0.78rem;
`;


const DocumentActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.55rem;
`;


const LifecycleEditor = styled.div`
  display: grid;
  gap: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px solid
    rgba(255, 255, 255, 0.08);
`;


const LifecycleCheckbox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: rgba(247, 247, 247, 0.78);
  font-size: 0.82rem;
`;
